import { SignupBotSignals } from 'src/app/core/util/signup-form/signup-form';
import { safeReturnUrl, takeReturnUrl } from 'src/app/core/util/return-url/return-url.util';
/**
 * @file This is the auth service module
 * @copyright Andreas Horvath 2016-2024
 * @version 0.2.0
 */
import { Injectable } from '@angular/core';
import { AuthBackendService } from '../../backend-services';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { MyUserService } from '../my-user/my-user.service';
import { MdbToastService } from '../mdb-toast';
import { MaintenanceService } from '../maintenance/maintenance.service';

export const GUEST_EXPIRED_MESSAGE = 'Dein Gastkonto ist abgelaufen, weil du länger nicht gespielt hast.';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public isAuthenticatedSource: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public isAuthenticated$: Observable<boolean> =
    this.isAuthenticatedSource.asObservable();

  public serverLogoutMessage = "";

  private hasLoaded: boolean = false;
  private isLoggedInAsGuest: boolean = false;
  private isLoggedIn: boolean = false;

  // An auth reply is only valid for the session in which its request started.
  private sessionVersion = 0;
  private serverLogoutGeneration = 0;
  private serverRevoked = false;
  private authCheck?: Promise<boolean>;
  private sessionChange?: Promise<unknown>;
  private logoutInFlight?: Promise<void>;

  constructor(
    private authBackendService: AuthBackendService,
    private router: Router,
    private toast: MdbToastService,
    private myUserService: MyUserService,
    private maintenanceService: MaintenanceService
  ) {}

  public async login(
    username: string,
    password: string,
    returnUrl?: string
  ): Promise<void> {
    if (!this.sessionChange && this.hasLoaded && this.isLoggedIn && !this.isLoggedInAsGuest) return;

    // Check if authentication is blocked due to maintenance
    if (this.isAuthBlocked()) {
      this.toast.warning('Anmeldung ist während der Wartung nicht möglich.');
      return;
    }

    try {
      await this.changeSession(
        () => this.authBackendService.login(username, password), true, false
      );
      if (returnUrl && returnUrl !== '/login') {
        await this.router.navigateByUrl(returnUrl);
      } else {
        await this.router.navigateByUrl('/dashboard');
      }
      this.myUserService.update();
    } catch (e) {
      if (e?.error?.code === 'accountDeletionPending') {
        this.myUserService.reset();
        this.setAuthState(false, false);
        await this.router.navigateByUrl('/konto-wiederherstellen');
        return;
      }
      console.log('Login error:', e);
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        const queryParams: any = {
          usernameOrPasswordWrong: e.error === 'usernameOrPasswordWrong',
          userLocked: e.error === 'userLocked',
          username: username,
          error:
            e.error !== 'usernameOrPasswordWrong' && e.error !== 'userLocked',
        };
        if (returnUrl) {
          queryParams.returnUrl = returnUrl;
        }
        this.router.navigate(['/login'], {
          queryParams,
        });
      });
    }
  }

  /** The server has already revoked this account's sessions. Do not POST logout
   * against a potentially newer cookie; clear the local state immediately. */
  public async handleServerLogout(reason?: string): Promise<void> {
    if (reason === 'accountDeletion') {
      this.serverLogoutMessage = 'Dein Konto wurde zur Löschung vorgemerkt. Eine gewählte Rücknahmefrist kannst du über die Anmeldung nutzen.';
    } else if (reason === 'guestExpired' || (!reason && this.isGuest())) {
      // No reason: an expired guest noticed by a rejected request instead of the cleanup job.
      this.serverLogoutMessage = GUEST_EXPIRED_MESSAGE;
    } else if (!this.serverRevoked) {
      this.serverLogoutMessage = 'Deine Sitzung wurde beendet. Bitte melde dich erneut an oder starte mit einem neuen Gastkonto.';
    }
    // The session watcher and game socket can report the same revocation.
    if (this.serverRevoked) return;
    this.serverLogoutGeneration++;
    this.serverRevoked = true;
    this.sessionVersion++;
    this.authCheck = undefined;
    this.myUserService.reset();
    this.setAuthState(false, false);
    await this.router.navigate(['/']);
  }

  public logout(): Promise<void> {
    if (this.logoutInFlight) return this.logoutInFlight;
    const logout = this.performLogout().finally(() => {
      if (this.logoutInFlight === logout) this.logoutInFlight = undefined;
    });
    this.logoutInFlight = logout;
    return logout;
  }

  private async performLogout(): Promise<void> {
    if (!this.sessionChange && this.hasLoaded && !this.isLoggedIn) {
      this.myUserService.reset();
      await this.router.navigate(['/']);
      return;
    }
    await this.changeSession(() => this.authBackendService.logout(), false, false);
    await this.router.navigate(['/']);
    this.toast.success('Du bist jetzt abgemeldet.');
  }

  public loginAsGuest(): Promise<void> {
    if (!this.sessionChange && this.hasLoaded && this.isLoggedIn) return Promise.resolve();

    // Check if authentication is blocked due to maintenance
    if (this.isAuthBlocked()) {
      this.toast.warning(
        'Gast-Anmeldung ist während der Wartung nicht möglich.'
      );
      return Promise.resolve();
    }

    return this.changeSession(() => this.authBackendService.guestLogin(), true, true)
      .then(() => {
        // no welcome toast: the dashboard's guest banner already says "Du spielst als Gast"
        this.router.navigateByUrl('/dashboard');
        this.myUserService.update();
      })
      .catch((e) => {
        console.log('Guest login error:', e);
        this.toast.error(
          'Gast-Anmeldung fehlgeschlagen. Bitte versuche es erneut.'
        );
      });
  }

  public async convertGuestToRegular(
    email: string,
    password: string,
    username?: string,
    returnUrl?: string,
    botSignals?: SignupBotSignals
  ): Promise<void> {
    // A rejected attempt clears the cached auth state: ask the server, a retry must not end silently.
    if (!await this.isAuthenticated() || !this.isLoggedInAsGuest) throw new Error('notAGuest');

    try {
      await this.changeSession(
        () => this.authBackendService.convertGuestToRegular(email, password, username, botSignals),
        true, false
      );
      this.myUserService.update();
      await this.router.navigateByUrl(safeReturnUrl(returnUrl) ?? '/dashboard');
      this.toast.success(
        'Dein Account wurde erfolgreich erstellt! Du bist jetzt ein regulärer Benutzer.'
      );
    } catch (e) {
      console.log('Guest conversion error:', e);
      throw e; // Re-throw für Component Error Handling
    }
  }

  public async completeGoogleRegistration(
    username: string,
    acceptTOS: boolean,
    acceptPrivacy: boolean
  ): Promise<void> {
    try {
      await this.changeSession(
        () => this.authBackendService.completeGoogleRegistration(username, acceptTOS, acceptPrivacy),
        true, false
      );
      this.myUserService.update();
      // back to where the Google sign-up started (e.g. /premium)
      await this.router.navigateByUrl(takeReturnUrl() ?? '/dashboard');
      this.toast.success(
        'Willkommen! Dein Account wurde erfolgreich erstellt.'
      );
    } catch (e) {
      console.log('Google registration completion error:', e);
      throw e; // Re-throw für Component Error Handling
    }
  }

  public async isAuthenticated(): Promise<boolean> {
    if (this.serverRevoked) return false;
    if (this.sessionChange) {
      // A failed mutation does not prove logout; check the server again below.
      await this.sessionChange.catch(() => undefined);
      return this.isAuthenticated();
    }
    if (this.hasLoaded) return this.isLoggedIn;
    if (this.authCheck) return this.authCheck;

    const version = this.sessionVersion;
    const check = this.authBackendService.checkLoggedIn().then(res => {
      if (version !== this.sessionVersion || this.sessionChange) {
        return this.isAuthenticated();
      }
      this.setAuthState(res.isLoggedIn, res.isGuest);
      return this.isLoggedIn;
    }).finally(() => {
      if (this.authCheck === check) this.authCheck = undefined;
    });
    this.authCheck = check;
    return check;
  }

  private setAuthState(loggedIn: boolean, guest: boolean): void {
    this.hasLoaded = true;
    this.isLoggedIn = loggedIn;
    this.isLoggedInAsGuest = loggedIn && guest;
    this.isAuthenticatedSource.next(loggedIn);
  }

  private changeSession<T>(request: () => Promise<T>, loggedIn: boolean, guest: boolean): Promise<T> {
    // Serialize mutations so a concurrent login/logout cannot race session cookies.
    // Only the request and state update are gated: navigation may itself await auth.
    const generation = this.serverLogoutGeneration;
    const previous = this.sessionChange ?? Promise.resolve();
    const operation = previous.catch(() => undefined).then(async () => {
      if (generation !== this.serverLogoutGeneration) throw new Error('Session invalidated');
      this.serverRevoked = false;
      const version = ++this.sessionVersion;
      this.authCheck = undefined;
      try {
        const result = await request();
        if (generation !== this.serverLogoutGeneration) throw new Error('Session invalidated');
        if (version === this.sessionVersion) {
          this.myUserService.reset();
          if (loggedIn) this.serverLogoutMessage = "";
          this.setAuthState(loggedIn, guest);
        }
        return result;
      } catch (error) {
        this.hasLoaded = false;
        throw error;
      }
    });
    const pending = operation.finally(() => {
      if (this.sessionChange === pending) this.sessionChange = undefined;
    });
    this.sessionChange = pending;
    return pending;
  }

  public isAuthBlocked(): boolean {
    const currentMode = this.maintenanceService.getCurrentMode();
    const isAdminBypass = this.maintenanceService.isCurrentUserAdmin();

    // Admin bypass: never block auth
    if (isAdminBypass) {
      return false;
    }

    // Server error: always block auth (server is down)
    if (currentMode === 'server_error') {
      return true;
    }

    // Planned maintenance: always block auth (no new logins during maintenance)
    if (currentMode === 'planned') {
      return true;
    }

    return false;
  }

  public getAuthBlockedMessage(): string {
    const currentMode = this.maintenanceService.getCurrentMode();
    if (currentMode === 'server_error') {
      return 'Anmeldung ist aufgrund von Serverproblemen derzeit nicht möglich.';
    } else if (currentMode === 'planned') {
      return 'Anmeldung ist während der Wartungsarbeiten nicht möglich.';
    }
    return '';
  }

  public isGuest(): boolean {
    return this.hasLoaded && this.isLoggedIn && this.isLoggedInAsGuest;
  }
}
